import { useEffect, useState, useCallback } from "react";
import { errorColor, successColor, useAuth } from "../../sdk";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

const getPayload = (value, index, isResavenue) => {
  return {
    RoomRatePlanId: value?.RoomRatePlanId,
    propertyId: value?.propertyId,
    roomId: value?.roomId,
    roomRatePlanName: isResavenue ? value?.roomRatePlanName : null,
    roomRateGroupCode: value?.roomRateGroupCode,
    discount: value?.discount ? Number(value?.discount) : null,
    promotionalDiscount: value?.promotionalDiscount
      ? Number(value?.promotionalDiscount)
      : null,
    roomName: value?.roomName,
    isBaseRoom: value?.isBaseRoom || false,
    addToBasePriceHigh: value?.addToBasePriceHigh
      ? Number(value?.addToBasePriceHigh)
      : null,
    addToBasePriceLow: value?.addToBasePriceLow
      ? Number(value?.addToBasePriceLow)
      : null,
    maxThresholdWeekday: value?.maxThresholdWeekday
      ? Number(value?.maxThresholdWeekday)
      : null,
    minThresholdWeekday: value?.minThresholdWeekday
      ? Number(value?.minThresholdWeekday)
      : null,
    maxThresholdWeekend: value?.maxThresholdWeekend
      ? Number(value?.maxThresholdWeekend)
      : null,
    minThresholdWeekend: value?.minThresholdWeekend
      ? Number(value?.minThresholdWeekend)
      : null,
    occupancyType: isResavenue ? value?.occupancyType : null,
    sequence: index + 1,
  };
};

export function useStaahRooms(isResavenue) {
  const { authFetch } = useAuth();
  const { hotelId } = useParams();
  const [roomsData, setRoomsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderRoomIndex, setLoaderRoomIndex] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [snackbarMsg, setSnackBarMsg] = useState({
    open: false,
    msg: "",
    color: "",
  });

  // Fetch initial rooms
  const fetchRooms = useCallback(async () => {
    if (!hotelId) return;
    try {
      setIsFetching(true);
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/get-room-rate-plan`,
      });

      const { data, response } = await get();
      if (data && response.status === 200) {
        setRoomsData(
          data.map((room) => ({
            ...room,
            uid: room.sequence || Date.now() + Math.random(),
            isNewRoom: false,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching rooms mapping", err);
    } finally {
      setIsFetching(false);
    }
  }, [authFetch, hotelId]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const changeHandler = (index, field, value) => {
    setRoomsData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isEdit: true, [field]: value };
      return updated;
    });
  };

  const handleUpdateRoomMapping = useCallback(
    async (index) => {
      const room = roomsData[index];
      setIsLoading(true);
      setLoaderRoomIndex(index);

      const payload = getPayload(room, index, isResavenue);

      try {
        const { put } = await authFetch({
          path: `/hotel/${hotelId}/update-room-rate-plan?ratePlanId=${room?.RoomRatePlanId}`,
        });

        const { response } = await put(payload);

        if (response.ok) {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.type,
            color: successColor,
          });
          fetchRooms();
        } else {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.messageToUser,
            color: errorColor,
          });
        }
      } catch (err) {
        setSnackBarMsg({
          open: true,
          color: errorColor,
          msg: "Something Went Wrong!",
        });
        console.error("Error Updating Room", err);
      } finally {
        setIsLoading(false);
        setLoaderRoomIndex(null);
      }
    },
    [authFetch, fetchRooms, hotelId, isResavenue, roomsData]
  );

  const handleCreateRoomMapping = useCallback(
    async (index) => {
      const room = roomsData[index];
      setIsLoading(true);

      const payload = getPayload(room, index, isResavenue);
      try {
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/create-room-rate-plan`,
        });

        const { response } = await post(payload);
        if (response.ok) {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.type,
            color: successColor,
          });
          fetchRooms();
        } else {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.messageToUser,
            color: errorColor,
          });
        }
      } catch (err) {
        setSnackBarMsg({
          open: true,
          msg: "Something Went Wrong!",
          color: errorColor,
        });
        console.error("Error in creating room mapping");
      } finally {
        setIsLoading(false);
      }
    },
    [authFetch, fetchRooms, hotelId, isResavenue, roomsData]
  );

  const handleSaveRooms = useCallback(
    (index) => {
      roomsData?.[index]?.isNewRoom
        ? handleCreateRoomMapping(index)
        : handleUpdateRoomMapping(index);
    },
    [handleCreateRoomMapping, handleUpdateRoomMapping, roomsData]
  );

  const updateRoomMappingSequence = useCallback(
    async (formerRoomRatePlanId, formerIdx, laterIdx) => {
      const payload = {
        formerId: formerRoomRatePlanId,
        formerSequence: formerIdx,
        laterSequence: laterIdx,
      };

      try {
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/room-rate-plan/sequence`,
        });

        const { response } = await post(payload);

        if (response.ok) {
          setSnackBarMsg({
            open: true,
            msg: "Sequence Updated Successfully",
            color: successColor,
          });
          fetchRooms();
        } else {
          setSnackBarMsg({
            open: true,
            msg: response?.data?.messageToUser || "Unable to update sequence",
            color: errorColor,
          });
        }
      } catch (error) {
        setSnackBarMsg({
          open: true,
          msg: "Something Went Wrong!",
          color: errorColor,
        });
        console.error("Error in Updating Room Mapping Sequence", error);
      }
    },
    [authFetch, fetchRooms, hotelId]
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = roomsData.findIndex((r) => r.uid === active.id);
    const newIndex = roomsData.findIndex((r) => r.uid === over.id);
    const activeRoomRatePlanId = roomsData[oldIndex]?.RoomRatePlanId;

    if (oldIndex === -1 || newIndex === -1) return;

    const updated = [...roomsData];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);

    // Reset sequence
    const reordered = updated.map((room, i) => ({ ...room, sequence: i + 1 }));
    setRoomsData(reordered);
    updateRoomMappingSequence(activeRoomRatePlanId, oldIndex + 1, newIndex + 1);
  };

  const isSaveForRoomDisabled = (index) => {
    const room = roomsData[index];
    return (
      !room?.roomId ||
      !room?.roomName ||
      !room?.roomRateGroupCode ||
      !room?.isEdit ||
      (isResavenue && (!room?.roomRatePlanName || !room?.occupancyType))
    );
  };

  const addRoom = () => {
    setRoomsData((prev) => [
      ...prev,
      {
        uid: Date.now() + Math.random(), // unique uid
        roomId: "",
        roomName: "",
        roomRateGroupCode: "",
        roomRatePlanName: "",
        promotionalDiscount: 0,
        occupancyType: "single",
        isBaseRatePlan: false,
        sequence: prev.length + 1,
        isNewRoom: true,
      },
    ]);
  };

  return {
    roomsData,
    changeHandler,
    handleSaveRooms,
    isSaveForRoomDisabled,
    isLoading,
    loaderRoomIndex,
    handleDragEnd,
    isFetching,
    fetchRooms,
    addRoom,
    snackbarMsg,
    setSnackBarMsg,
  };
}
